import {
  Autocomplete,
  Box,
  Button,
  List,
  Stack,
  TextField,
  Tooltip,
  Divider,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import ClearIcon from "@mui/icons-material/Clear"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import { useEffect, useRef, useState } from "react"
import DeleteIcon from "@mui/icons-material/Delete"
import DownloadIcon from "@mui/icons-material/Download"
import { saveAs } from "file-saver"
import EditIcon from "@mui/icons-material/Edit"
import db from "../Firestore"
import { serverTimestamp } from "../pages/services/dataServices"
import FileItem from "./FileItem"
import Heading from "./controls/Heading"
import TemplateInstanceHeadings from "./TemplateInstanceHeadings"
import * as dataServices from "../pages/services/dataServices"
import {
  createOpenAIFile,
  createOpenAIVectorStore,
  deleteOpenAIFile,
} from "../pages/services/cloudFunctions"
import { useSnackbar } from "notistack"
import { getUrl, uploadFiles } from "../pages/services/fileServices"
import { deleteFile } from "../pages/services/modelServices"
import { generateDoc } from "../pages/services/templateInstanceDocGenerator"
import { Packer } from "docx"
import useConfirmationDialog from "./useConfirmationDialog"

const styles = {
  input: {},
}

const ModelEditDashboardDocumentsTab = ({
  accountId,
  viewSetId,
  viewSet,
  views,
  setValues,
  handleAddSuggestedView,
  handleShowCreateContentWithQuestion
}) => {
  const [templateSelection, setTemplateSelection] = useState([])

  const [templateOptions, setTemplateOptions] = useState([])

  const [templateInstances, setTemplateInstances] = useState([])

  const [templateInstance, setTemplateInstance] = useState()

  const { ConfirmationDialog, confirm } = useConfirmationDialog()

  const addFileRef = useRef()

  const { enqueueSnackbar } = useSnackbar()

  // Liaten for template_instances changes
  useEffect(() => {
    if (accountId) {
      const unsub = db
        .collection("template_instances")
        .where("account_id", "==", accountId)
        .where("parent_type", "==", "view_set")
        .where("parent_id", "==", viewSetId)
        .onSnapshot((querySnapshot) => {
          const templateInstances = []
          querySnapshot.forEach((doc) => {
            templateInstances.push({ id: doc.id, ...doc.data() })
          })
          setTemplateInstances(templateInstances)
        })
      return () => unsub()
    }
  }, [accountId, viewSetId])

  useEffect(() => {
    if (accountId) {
      db.collection("templates")
        .where("account_id", "==", accountId)
        .get()
        .then((querySnapshot) => {
          const templates = []
          querySnapshot.forEach((doc) => {
            templates.push({ id: doc.id, label: doc.data().name })
          })
          const sortedTemplates = templates.sort((a, b) =>
            a.label.localeCompare(b.label)
          )
          console.log("templates", sortedTemplates)
          setTemplateOptions(sortedTemplates)
        })
    }
  }, [accountId])

  const handleUploadFileToOpenAI = async (doc) => {
    // Get first file in the list, if any

    console.log("%c[create file] create openai file", "color:yellow", { doc })

    // Load document from cloud storage
    const folderPath = `accounts/${viewSet.account_id}/view_sets/${viewSetId}`
    const fullPath = `${folderPath}/${doc.name}`

    console.log("%c[create file] full path", "color:orange", { fullPath })

    const createFileResult = await createOpenAIFile({
      fileStoragePath: fullPath,
    })
    console.log("create file result", { createFileResult })

    const fileId = createFileResult.data.response.id
    if (!fileId) {
      throw new Error(
        "Failed to create file - " + JSON.stringify(createFileResult)
      )
    }

    console.log("%cfile id", "color:blue", { fileId, fullPath })

    // Each file goes into its own vector store id, so that individual files can be selected to be applied to gen ai.
    // Pass the newly created file id in so the vector store is created with the file already attached.

    const vsResult = await createOpenAIVectorStore({
      name: `File ${doc.name} - Account ${viewSet.account_id}`,
      fileIds: [fileId],
    })
    console.log("create vector store result", { vsResult })
    const fileVsId = vsResult.data.response.id
    if (!fileVsId) {
      throw new Error(
        "Failed to create vector store - " + JSON.stringify(vsResult)
      )
    }

    console.log("%cfile vector store id", "color:blue", {
      vectorStoreId: fileVsId,
      fullPath,
    })

    // Update file id into docs array

    const newDocs = viewSet.docs.map((d) => {
      if (d.name === doc.name) {
        return {
          ...d,
          file_id: fileId,
          vector_store_id: fileVsId,
        }
      } else {
        return d
      }
    })

    console.log("%cnewDocs", "color:orange", { newDocs })

    setValues({ ...viewSet, docs: newDocs })

    await db.collection("view_sets").doc(viewSetId).update(
      {
        docs: newDocs,
        modified: dataServices.serverTimestamp(),
      },
      { merge: true }
    )

    enqueueSnackbar("Uploaded to OpenAI", { variant: "success" })
  }

  const handleRemoveFileFromOpenAI = async (doc) => {
    console.log("Removing file from OpenAI", { doc })

    if (doc.file_id) {
      deleteOpenAIFile({ fileId: doc.file_id }).then((result) => {
        console.log("delete file result", { result })

        // Update file id into docs array
        const newDocs = viewSet.docs.map((d) => {
          if (d.name === doc.name) {
            // remove file_id attribute
            const { file_id, ...rest } = d
            return rest
          } else {
            return d
          }
        })

        setValues({ ...viewSet, docs: newDocs })

        db.collection("view_sets")
          .doc(viewSetId)
          .update({ docs: newDocs, modified: dataServices.serverTimestamp() })
          .then(() => {
            enqueueSnackbar("Removed from OpenAI", { variant: "success" })
          })
      })
    }
  }

  const handleDeleteFile = async (index) => {
    const fileItem = viewSet.docs[index]

    const name = fileItem.name

    const confirmed = await confirm({
      title: "Delete File?",
      description: `Delete file ${name}?`,
      yesText: "Delete",
      yesColor: "error",
      noText: "Cancel",
    })

    if (!confirmed) {
      return
    }

    console.log("handleDeleteFile", { name })
    const newDocs = viewSet.docs.filter((doc) => doc.name !== name)

    setValues({ ...viewSet, docs: newDocs })

    const folderPath = `accounts/${viewSet.account_id}/view_sets/${viewSetId}`
    await deleteFile(folderPath, name)

    if (viewSetId) {
      db.collection("view_sets")
        .doc(viewSetId)
        .update({ docs: newDocs, modified: dataServices.serverTimestamp() })
        .then(() => {
          enqueueSnackbar("Deleted", { variant: "success" })
        })
    }
  }

  const handleAddFile = async (event) => {
    const filePath = `accounts/${viewSet.account_id}/view_sets/${viewSetId}/`
    const files = []
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i]
      files.push(file)
    }

    console.log("%cuploading files", "color:lightgreen", { files, filePath })
    await uploadFiles(files, filePath)

    // Merge files into the values.docs array, with each element having a name, and date uploaded. Deduplicate by name.

    const newDocs = viewSet.docs || []

    files.forEach((file) => {
      const doc = {
        name: file.name,
      }

      newDocs.push(doc)
    })

    // Update docs in state, and also in DB

    setValues({ ...viewSet, docs: newDocs })

    if (viewSetId) {
      db.collection("view_sets")
        .doc(viewSetId)
        .update({ docs: newDocs, modified: dataServices.serverTimestamp() })
        .then(() => {
          enqueueSnackbar("Uploaded", { variant: "success" })
        })
    }

    addFileRef.current.value = null
  }

  const handleDownload = async (index) => {
    const doc = viewSet.docs[index]
    const folderPath = `accounts/${viewSet.account_id}/view_sets/${viewSetId}/`
    const url = await getUrl(folderPath, doc.name)
    saveAs(url, doc.name)
  }

  const handleDownloadWordDoc = async (templateInstance) => {
    console.log("Downloading word doc", { templateInstance })

    const doc = generateDoc(templateInstance)
    console.log("generated doc", doc)
    const blob = await Packer.toBlob(doc)

    // Use file-saver to save the file
    saveAs(blob, `${templateInstance.name}.docx`)
  }

  const handleUpload = (event) => {
    event.preventDefault()
    addFileRef.current.click()
  }

  const handleDeletePrompt = async (templateInstanceToDelete) => {
    const confirmed = await confirm({
      title: "Delete Document?",
      description: "This delete is permanent. Are you sure?",
      yesText: "Delete",
      yesColor: "error",
      noText: "Cancel",
    })

    if (!confirmed) {
      return
    }

    console.log("Deleting", templateInstanceToDelete)

    if (templateInstanceToDelete) {
      // Delete template
      db.collection("template_instances")
        .doc(templateInstanceToDelete.id)
        .delete()
        .then(() => {
          enqueueSnackbar("Deleted", { variant: "success" })
        })
        .then(() => {
          if (templateInstanceToDelete.id === templateInstance?.id) {
            setTemplateInstance(undefined)
          }
        })
    }
  }

  const handleSaveHeadingContent = async ({
    selectedHeading,
    headingContent,
  }) => {
    const newTemplateInstance = templateInstance.content.map((heading) => {
      if (heading.level === selectedHeading.level) {
        return { ...heading, content: headingContent }
      } else {
        return heading
      }
    })

    setTemplateInstance({ ...templateInstance, content: newTemplateInstance })

    db.collection("template_instances")
      .doc(templateInstance.id)
      .update({
        content: newTemplateInstance,
        modified: serverTimestamp(),
      })
      .then(() => {
        enqueueSnackbar("Saved", { variant: "success" })
      })
  }

  const handleAddTemplates = async () => {
    for (const selection of templateSelection) {
      console.log("Adding template", selection)

      const templateData = await db
        .collection("templates")
        .doc(selection.id)
        .get()
      const template = templateData.data()

      const newTemplateInstance = {
        account_id: accountId,
        template_id: selection.id,
        name: template.name,
        content: template.headings,
        parent_type: "view_set",
        parent_id: viewSetId,
        created: serverTimestamp(),
        modified: serverTimestamp(),
      }

      console.log("%cadding template", "color:lightgreen", newTemplateInstance)

      db.collection("template_instances")
        .add(newTemplateInstance)
        .then((docRef) => {
          console.log("Document written with ID: ", docRef.id)
        })
        .catch((error) => {
          console.error("Error adding document: ", error)
        })
    }
  }

  const handleSelectFile = (file) => {
    console.log("Selected file", file)
    setTemplateInstance(file)
  }

  return (
    <>
      <ConfirmationDialog />

      <Stack gap={1}>
        <Box>
          <Heading>Input Files</Heading>
        </Box>

        {!viewSet.docs || viewSet.docs.length === 0 ? (
          <Typography variant="body2">No files uploaded currently</Typography>
        ) : null}

        <List dense>
          {viewSet.docs &&
            viewSet.docs.map((doc, index) => (
              <FileItem
                key={doc.name}
                fileName={doc.name}
                fileIndex={index}
                handlePromptDeleteFile={handleDeleteFile}
                handleDownload={handleDownload}
                showSecondaryText={() => {
                  return doc.file_id ? (
                    <Typography variant="caption" color="primary">
                      Loaded in OpenAI
                    </Typography>
                  ) : (
                    <Typography color="warning" variant="caption">
                      Not in OpenAI. You need to upload it to OpenAI to use it.
                    </Typography>
                  )
                }}
                extraMenuItems={[
                  {
                    label: "Edit",
                    icon: <EditIcon />,
                    onClick: () => console.log("Edit", index),
                  },
                  {
                    label: "Delete from OpenAI",
                    icon: <DeleteIcon />,
                    onClick: () => handleDeletePrompt(templateInstances[index]),
                    disabled: Boolean(!doc.file_id),
                  },
                  {
                    icon: <UploadFileIcon />,
                    label: "Upload to OpenAI",
                    disabled: Boolean(doc.file_id),
                    onClick: () => {
                      console.log("uploading to OpenAI", { doc })
                      handleUploadFileToOpenAI(doc)
                    },
                  },
                  {
                    icon: <ClearIcon />,
                    label: "Remove from OpenAI",
                    disabled: Boolean(!doc.file_id),
                    onClick: () => {
                      console.log("Remove file from OpenAI", { doc })
                      handleRemoveFileFromOpenAI(doc)
                    },
                  },
                ]}
              />
            ))}
        </List>

        <Box sx={{ py: "5px" }}>
          <Button
            type="button"
            variant="contained"
            tooltip="Upload a file"
            endIcon={<CloudUploadIcon />}
            onClick={handleUpload}
          >
            Upload
          </Button>
        </Box>

        <input
          hidden
          accept="*/*"
          sx={styles.input}
          id="icon-button-file"
          type="file"
          capture="environment"
          ref={addFileRef}
          onChange={(e) => handleAddFile(e)}
        />

        <Box>
          <Divider />
        </Box>

        <Heading>Output Documents</Heading>

        <List dense>
          {templateInstances &&
            templateInstances.map((doc, index) => (
              <FileItem
                key={`${doc.name}-${index}`}
                fileName={doc.name}
                fileIndex={index}
                handleSelectFile={(index) =>
                  handleSelectFile(templateInstances[index])
                }
                handleDownload={() =>
                  handleDownloadWordDoc(templateInstances[index])
                }
                extraMenuItems={[
                  {
                    label: "Delete",
                    icon: <DeleteIcon />,
                    onClick: () => handleDeletePrompt(templateInstances[index]),
                  },
                  {
                    label: "Download",
                    icon: <DownloadIcon />,
                    onClick: () =>
                      handleDownloadWordDoc(templateInstances[index]),
                  },
                ]}
              />
            ))}
        </List>

        <Divider />

        <Stack direction="row" gap={1} sx={{ mt: 2 }}>
          <Tooltip title="Add a template, and then populate it with content from views">
            <span>
              <Button
                onClick={handleAddTemplates}
                variant="outlined"
                sx={{ textTransform: "none" }}
                endIcon={<AddIcon />}
              >
                Add Template(s)
              </Button>
            </span>
          </Tooltip>
          <Box sx={{ minWidth: "300px" }}>
            {templateOptions.length > 0 && (
              <Autocomplete
                multiple
                id="template"
                options={templateOptions}
                size="small"
                value={templateSelection}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, newValue) => {
                  setTemplateSelection(newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template"
                    variant="outlined"
                    size="small"
                    sx={{ maxWidth: "300px" }}
                  />
                )}
              />
            )}
          </Box>
        </Stack>

        <Heading>Selected Document</Heading>
        <Typography variant="h6">{templateInstance?.name}</Typography>
        {templateInstance && (
          <TemplateInstanceHeadings
            templateInstance={templateInstance}
            viewSet={viewSet}
            views={views}
            handleSaveHeadingContent={handleSaveHeadingContent}
            handleAddSuggestedView={handleAddSuggestedView}
            handleShowCreateContentWithQuestion={handleShowCreateContentWithQuestion}
          />
        )}
      </Stack>
    </>
  )
}

export default ModelEditDashboardDocumentsTab
